/*
 * - ROOT
 *   - MAIN
 * - STORE_SELECTION_FLOW
 */

export enum AppRoute {
  // Structural ---------------------------------------------------------------
  // Implementation of React Navigation's "Authentication flow"
  // https://reactnavigation.org/docs/auth-flow/
  // (in this case not for "authentication" but for "store selection")
  // RootStack > ROOT (DrawerNavigator)
  // ROOT is the "app" when a store is already selected (a store needs to be
  // selected before the user can use the "app")
  // We have two main levels of navigation:
  // 1. Drawer
  // 2. Bottom tabs (in Web is really a Stack Navigator)
  // Every normal route in the app is a child of "2. Bottom tabs", but some
  // routes have "links/entries" in "1. Drawer" (and have the bottom tab
  // button hidden). But anyway, because this is hierarchical and "2. Bottom
  // tabs" is a child of "1. Drawer", the full route of most of the routes is:
  // ROOT > MAIN > <Route>
  ROOT = 'ROOT',

  // RootStack > ROOT_STORE_SELECTION_FLOW
  // ROOT_STORE_SELECTION_FLOW is the store selection flow. A store needs to be
  // selected before the user can use the "app"
  ROOT_STORE_SELECTION_FLOW = 'ROOT_STORE_SELECTION_FLOW',

  // RootStack > ROOT_REWARDS_FLOW
  // ROOT_REWARDS_FLOW is the rewards sign up flow.
  // A user will receive a temporary code for use in store
  ROOT_REWARDS_FLOW = 'ROOT_REWARDS_FLOW',

  // RootStack > ROOT_PROMOTIONS_FLOW
  // ROOT_PROMOTIONS_FLOW is for promotion-related screens that don't require store selection
  ROOT_PROMOTIONS_FLOW = 'ROOT_PROMOTIONS_FLOW',

  /**
   * Checkout flow broken out from main
   */
  ROOT_CHECKOUT_FLOW = 'ROOT_CHECKOUT_FLOW',

  // Browse Storybook components for development
  ROOT_STORYBOOK = 'ROOT_STORYBOOK',

  // View the build info including last commit hash and date
  ROOT_APP_BUILD_INFO = 'ROOT_APP_BUILD_INFO',

  // ----------------------------------------------------------------------------
  // "App" ----------------------------------------------------------------------
  // RootStack > ROOT (DrawerNavigator) > MAIN (BottomTabNavigator)
  // Every normal route in the app is a child of MAIN (even if its tab button is
  // hidden and its reachable through the parent drawer)
  // Yes, React Navigation recommends "to reduce nesting navigators to minimal"
  // (https://reactnavigation.org/docs/nesting-navigators/). The fact that we
  // have both a drawer and bottom tabs visible all the time brings complexity
  // to the navigation
  MAIN = 'MAIN',
  GIFT_CARDS_MAIN = 'GIFT_CARDS_MAIN',
  // ----------------------------------------------------------------------------
  // "App" routes ---------------------------------------------------------------
  // Reachable through "Drawer"
  MY_STORE = 'MY_STORE',
  ACCOUNT_NOT_LOGGED_IN = 'ACCOUNT_NOT_LOGGED_IN',
  ACCOUNT_LOGGED_IN = 'ACCOUNT_LOGGED_IN',
  ACCOUNT = 'ACCOUNT',
  WISHLIST = 'WISHLIST',
  PROMOTIONS = 'PROMOTIONS',
  CONTACT_US = 'CONTACT_US',
  INFORMATION = 'INFORMATION',
  RESPONSIBLE_SERVICE = 'RESPONSIBLE_SERVICE',
  SELF_EXCLUSION = 'SELF_EXCLUSION',

  // Reachable through "Bottom tabs"
  HOME = 'HOME',
  SHOP = 'SHOP',
  DISCOVER = 'DISCOVER',
  CATALOGUE = 'CATALOGUE',
  CART = 'CART',

  /**
   * Rewards bottom tab screen
   */
  REWARDS = 'REWARDS',

  // Reachable through the "header"
  NEW_SEARCH = 'NEW_SEARCH',

  // "Screens"
  ARTICLE = 'ARTICLE',
  PROMOTION = 'PROMOTION',
  PROMOTION_IFRAME = 'PROMOTION_IFRAME',
  PRODUCT = 'PRODUCT',
  SUB_DEPARTMENT = 'SUB_DEPARTMENT',
  DEPARTMENT = 'DEPARTMENT',
  FAVOURITE_BRANDS = 'FAVOURITE_BRANDS',
  BRAND = 'BRAND',

  /**
   * Rewards offer detail screen (Exclusive offers)
   */
  EXCLUSIVE_OFFER_DETAIL = 'EXCLUSIVE_OFFER_DETAIL',

  /**
   * Rewards offer list screen (Exclusive offers)
   */
  EXCLUSIVE_OFFER_LIST = 'EXCLUSIVE_OFFER_LIST',

  /**
   * Members price list screen
   */
  MEMBERS_PRICE_LIST = 'MEMBERS_PRICE_LIST',

  // some of these "CART" could be one stack?
  CART_ORDER_CHECKOUT = 'CART_ORDER_CHECKOUT',
  CART_ORDER_CHECKOUT_REVIEW = 'CART_ORDER_CHECKOUT_REVIEW',
  CART_ORDER_CHECKOUT_PAYMENT = 'CART_ORDER_CHECKOUT_PAYMENT',
  CART_ORDER_PROCESSING = 'CART_ORDER_PROCESSING',
  CART_ORDER_COMPETITIONS = 'CART_ORDER_COMPETITIONS',
  CART_ORDER_CONFIRMATION = 'CART_ORDER_CONFIRMATION',
  PROMOTIONS_CURRENT = 'PROMOTIONS_CURRENT',
  PROMOTIONS_WINNERS = 'PROMOTIONS_WINNERS',
  PROMOTION_WINNERS_DETAIL = 'PROMOTION_WINNERS_DETAIL',
  PROMOTIONS_OPT_OUT = 'PROMOTIONS_OPT_OUT',
  CONTACT_US_SUBMIT_TICKET = 'CONTACT_US_SUBMIT_TICKET',
  INFORMATION_CONTENT = 'INFORMATION_CONTENT',
  ACCOUNT_DELETE_INFO = 'ACCOUNT_DELETE_INFO',
  RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS = 'RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS',
  RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST = 'RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST',

  // Store
  MY_STORE_SUBURB_SELECTION = 'MY_STORE_SUBURB_SELECTION',
  MY_STORE_STORE_SELECTION = 'MY_STORE_STORE_SELECTION',
  ROOT_GIFT_CARDS_PROMOTION = 'ROOT_GIFT_CARDS_PROMOTION',
  ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS = 'ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS',

  // Store switch through deep link
  STORE_SWITCH_LINK = 'STORE_SWITCH_LINK',

  // Account
  // some of these "ACCOUNT" could be one stack?
  ACCOUNT_LOGIN = 'ACCOUNT_LOGIN',
  ACCOUNT_CREATE = 'ACCOUNT_CREATE',
  ACCOUNT_DELETE = 'ACCOUNT_DELETE',
  ACCOUNT_DETAIL = 'ACCOUNT_DETAIL',
  ACCOUNT_COLLECT_MISSING_DETAILS = 'ACCOUNT_COLLECT_MISSING_DETAILS',
  ACCOUNT_CHANGE_PASSWORD = 'ACCOUNT_CHANGE_PASSWORD',

  /**
   * This is the screen in account management. When hasRewards=false shows similar to the more rewards tab REWARDS.
   */
  ACCOUNT_REWARDS = 'ACCOUNT_REWARDS',
  ACCOUNT_COMMUNICATIONS_PREFERENCES = 'ACCOUNT_COMMUNICATIONS_PREFERENCES',
  ACCOUNT_PASSWORD_RESET = 'ACCOUNT_PASSWORD_RESET',
  ACCOUNT_RECENT_ORDERS = 'ACCOUNT_RECENT_ORDERS',
  ACCOUNT_ORDER_VIEW = 'ACCOUNT_ORDER_VIEW',
  ACCOUNT_PAYMENT_METHOD = 'ACCOUNT_PAYMENT_METHOD',

  // Loyalty scope of work introduces new account routes

  /**
   * Account create flow - phone verification. This is added as part of loyalty
   */
  ACCOUNT_PHONE_VERIFICATION = 'ACCOUNT_PHONE_VERIFICATION',

  /*
   * Account create flow - email verification for existing accounts logging in again.
   * This is added as part of loyalty
   */
  ACCOUNT_EMAIL_VERIFICATION = 'ACCOUNT_EMAIL_VERIFICATION',

  /**
   * Account create flow - success screen. This is added as part of loyalty
   */
  ACCOUNT_SUCCESS = 'ACCOUNT_SUCCESS',

  // 3DS
  THREEDS_IFRAME_FULLSCREEN = 'THREEDS_IFRAME_FULLSCREEN',

  // Gift Cards
  // In regular (liquor) stores
  GIFT_CARDS_OPTIONS = 'GIFT_CARDS_OPTIONS',
  GIFT_CARDS_PARTICIPATING_STORES = 'GIFT_CARDS_PARTICIPATING_STORES',
  GIFT_CARDS_PROMOTION_WAITER = 'GIFT_CARDS_PROMOTION_WAITER',

  // In gift cards stores
  GIFT_CARDS_HOME = 'GIFT_CARDS_HOME',
  GIFT_CARDS_STORE_REVIEW = 'GIFT_CARDS_STORE_REVIEW',
  GIFT_CARDS_STORE_CHECKOUT = 'GIFT_CARDS_STORE_CHECKOUT',
  GIFT_CARDS_STORE_SECRET_CART = 'GIFT_CARDS_STORE_SECRET_CART',
  GIFT_CARDS_STORE_ORDER_CONFIRMATION = 'GIFT_CARDS_STORE_ORDER_CONFIRMATION',

  // In all stores
  GIFT_CARDS_CHECK_BALANCE = 'GIFT_CARDS_CHECK_BALANCE',
  GIFT_CARDS_HOW_TO_REDEEM = 'GIFT_CARDS_HOW_TO_REDEEM',
  GIFT_CARDS_TERMS_AND_CONDITIONS = 'GIFT_CARDS_TERMS_AND_CONDITIONS',

  // Rewards
  REWARDS_SIGN_IN = 'REWARDS_SIGN_IN',
  REWARDS_WELCOME_BACK = 'REWARDS_WELCOME_BACK',
  REWARDS_STAFF_STORE_SELECTION = 'REWARDS_STAFF_STORE_SELECTION',
  REWARDS_LMG_STAFF_SELECTION = 'REWARDS_LMG_STAFF_SELECTION',
  REWARDS_SIGN_UP = 'REWARDS_SIGN_UP',
  REWARDS_VERIFICATION = 'REWARDS_VERIFICATION',
  REWARDS_SUCCESS = 'REWARDS_SUCCESS',
  // DIS-1329: terminal block when staff promotion is skipped (Zen↔Zoho mismatch).
  REWARDS_STAFF_ACTIVATION_BLOCKED = 'REWARDS_STAFF_ACTIVATION_BLOCKED',
  // DIS-1328: terminal screen when a non-authoritative sign-in returns HTTP 422
  // account_not_found (no Zoho contact for the phone).
  REWARDS_ACCOUNT_NOT_FOUND = 'REWARDS_ACCOUNT_NOT_FOUND',
  REWARDS_TERMS_AND_CONDITIONS = 'REWARDS_TERMS_AND_CONDITIONS',
  REWARDS_FULL_SIGN_UP = 'REWARDS_FULL_SIGN_UP',
  REWARDS_FULL_SUCCESS = 'REWARDS_FULL_SUCCESS',

  /**
   * Activate rewards flow
   * 1. show terms and conditions
   */
  REWARDS_ACTIVATE_TERMS_AND_CONDITIONS = 'REWARDS_ACTIVATE_TERMS_AND_CONDITIONS',

  /**
   * 2. phone number verification
   */
  REWARDS_ACTIVATE_VERIFICATION = 'REWARDS_ACTIVATE_VERIFICATION',

  /**
   * 3. success page
   */
  REWARDS_ACTIVATE_SUCCESS = 'REWARDS_ACTIVATE_SUCCESS',

  /**
   * Screen to inform user they need to login and opt in to join rewards
   */
  REWARDS_REDIRECT = 'REWARDS_REDIRECT',

  /**
   * Storybook component browser
   */
  STORYBOOK = 'STORYBOOK',

  /**
   * Developer settings
   */
  DEVELOPER_SETTINGS = 'DEVELOPER_SETTINGS',

  // TMP
  SUMMER_CASH_GRAB_GAME = 'SUMMER_CASH_GRAB_GAME',
}
